import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { DecimalInput } from '@/modules/common/components/molecules/inputs/decimal-form-field';

import { useAdjustMemberTimeOffBalance } from '../../hooks/use-adjust-member-time-off-balance';
import { TimeOffBalance } from '../../types/time-off-balance';

const FIELD_AVAILABLE = 'available';
const MIN_AVAILABLE_DAYS = -366;
const MAX_AVAILABLE_DAYS = 366;
const MAX_DECIMAL_PLACES = 2;

const buildSchema = (t: (key: string) => string) =>
  z.object({
    [FIELD_AVAILABLE]: z
      .string()
      .refine(value => value !== '' && value !== '-' && Number.isFinite(Number(value)), {
        message: t('member.adjust.errors.invalid'),
      })
      .refine(value => Number(value) >= MIN_AVAILABLE_DAYS, {
        message: t('member.adjust.errors.min'),
      })
      .refine(value => Number(value) <= MAX_AVAILABLE_DAYS, {
        message: t('member.adjust.errors.max'),
      })
      .refine(value => /^-?\d+(?:\.\d{1,2})?$/.test(value), {
        message: t('member.adjust.errors.precision'),
      }),
  });

type AdjustBalanceFormValues = { available: string };

interface AdjustBalanceModalProps {
  memberId: string;
  /** The balance being edited; null when the modal is closed. */
  balance: TimeOffBalance | null;
  open: boolean;
  onClose: () => void;
}

/**
 * Modal to set the absolute "Available" days for a member's time-off balance
 * type. RHF + Zod per project convention. Pre-fills the current available value.
 */
export const AdjustBalanceModal = ({
  memberId,
  balance,
  open,
  onClose,
}: AdjustBalanceModalProps) => {
  const { t } = useTranslation(['authenticated.timeOff', 'common']);

  const form = useForm<AdjustBalanceFormValues>({
    resolver: zodResolver(buildSchema(t)),
    defaultValues: { available: '0' },
  });

  // Re-seed the field with the current available value each time a balance opens.
  useEffect(() => {
    if (balance) {
      form.reset({ available: String(balance.availableDays) });
    }
  }, [balance, form]);

  const adjustMutation = useAdjustMemberTimeOffBalance(memberId, {
    onSuccess: onClose,
  });

  const handleSubmit = form.handleSubmit(values => {
    if (!balance) return;
    adjustMutation.mutate({
      typeId: balance.type.id,
      newAvailable: Number(values.available),
      previousAvailable: balance.availableDays,
    });
  });

  return (
    <Modal
      open={open}
      onOpen={() => {}}
      onClose={onClose}
      preventClose={adjustMutation.isPending}
      className="max-w-md"
    >
      <Modal.Title className="mb-1">{balance?.type.displayName}</Modal.Title>
      <Modal.Body className="mb-4 py-0 text-sm text-muted-foreground">
        {t('member.adjust.subtitle')}
      </Modal.Body>

      <Form {...form}>
        <form onSubmit={handleSubmit}>
          <Modal.Body className="mb-4 py-0">
            <FormField
              control={form.control}
              name={FIELD_AVAILABLE}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('member.adjust.availableLabel')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <DecimalInput
                        className="pr-16"
                        name={field.name}
                        value={field.value}
                        maxDecimalPlaces={MAX_DECIMAL_PLACES}
                        maxIntegerDigits={3}
                        allowNegative
                        onBlur={field.onBlur}
                        onValueChange={value => {
                          const numericValue = Number(value);
                          const isIncompleteNegativeValue = value === '-';
                          const isWithinRange =
                            numericValue >= MIN_AVAILABLE_DAYS &&
                            numericValue <= MAX_AVAILABLE_DAYS;

                          if (isIncompleteNegativeValue || isWithinRange) {
                            field.onChange(value);
                          }
                        }}
                      />
                      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                        {t('member.adjust.daysSuffix')}
                      </span>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Modal.Body>

          <Modal.Footer>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={adjustMutation.isPending}
              >
                {t('common:cancel')}
              </Button>
              <ButtonLoadable type="submit" isLoading={adjustMutation.isPending}>
                {t('member.adjust.apply')}
              </ButtonLoadable>
            </div>
          </Modal.Footer>
        </form>
      </Form>
    </Modal>
  );
};
